import { analytics } from '@qbe/qal';
import { EcommercePurchasePayload } from '@qbe/qal/src/types';

import type { RootState } from '../../store';
import mapStore from './mapStore';
import { PageAction, pageMappings } from './pageMappings';
import { qalECommerceGA4 } from './types';

/**
 * Applies actions based on conditions and returns updated values for path, storePayload, and ecommerce.
 *
 * @param actions - An array of PageAction objects representing actions to be applied.
 * @param state - The RootState object representing the current state of the application.
 * @param path - The current path.
 * @param storePayload - The current store payload.
 * @param ecommerce - The current ecommerce data.
 * @returns An object with updated values for path, storePayload, and ecommerce.
 */
const applyActions = (
  actions: PageAction[] | undefined,
  state: RootState,
  path: string,
  storePayload: Record<string, unknown>,
  ecommerce: EcommercePurchasePayload
) => {
  let updatedPath = path;
  let updatedStorePayload = { ...storePayload };
  let updatedEcommerce = ecommerce;

  if (actions) {
    actions.forEach((action) => {
      if (action.test !== undefined) {
        const isMatch =
          typeof action.test === 'string' ? action.test === path : action.test.test(path);

        if (!isMatch) return;
      }

      if (action.method) {
        action.method(state);
      } else if (action.newPath) {
        updatedPath = action.newPath;
      }

      if (action.dataPoints) {
        updatedStorePayload = { ...updatedStorePayload, ...action.dataPoints };
      }

      if (action.ecommerce) {
        const { policyNumber, premiums } = state.quote;
        const { paymentFrequency } = state.payments || {};
        const industryDetails = state.businessDetails || {};
        const industryValue = industryDetails?.industry?.industryValue || '';
        if (policyNumber && premiums && paymentFrequency && industryValue) {
          updatedEcommerce = qalECommerceGA4(
            policyNumber,
            premiums,
            paymentFrequency,
            industryValue
          );
        }
      }
    });
  }

  return { path: updatedPath, storePayload: updatedStorePayload, ecommerce: updatedEcommerce };
};

/**
 * Applies common payload to the store payload.
 *
 * @param commonPayload - The common payload to be applied.
 * @param storePayload - The current store payload.
 * @returns The updated store payload.
 */
const applyCommonPayload = (
  commonPayload: Record<string, unknown>,
  storePayload: Record<string, unknown>
) => ({ ...storePayload, ...commonPayload });

/**
 * Tracks a page view using the QAL analytics library.
 *
 * @param state - The RootState object representing the current state of the application.
 * @param path - The current path.
 */
export const qalTrackPageView = (state: RootState, path: string = '') => {
  if (path === '/') {
    // If path is '/', return early without further processing
    return;
  }

  let storePayload = mapStore(state);
  let ecommerce = {} as EcommercePurchasePayload;

  Object.keys(pageMappings).forEach((mappingKey) => {
    const mapping = pageMappings[mappingKey];

    if (mapping.condition(path)) {
      const {
        path: newPath,
        storePayload: newStorePayload,
        ecommerce: newEcommerce,
      } = applyActions(mapping.actions, state, path, storePayload, ecommerce);

      storePayload = newStorePayload;

      if (mapping.commonPayload) {
        storePayload = applyCommonPayload(mapping.commonPayload, storePayload);
      }
      path = newPath;
      ecommerce = newEcommerce;
    }
  });

  analytics.trackPageView({
    virtualPagePath: path,
    data: { ...storePayload },
    ecommerce,
  });
};
